import { useEffect, useMemo, useState } from "react";
import { X, Trash2, MapPin, FileText, User as UserIcon, Folder, Bell, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DictaphoneTextarea } from "@/components/DictaphoneTextarea";

const REMINDER_OPTIONS = [
  { v: null, l: "Keine Erinnerung" },
  { v: 10, l: "10 Minuten vorher" },
  { v: 30, l: "30 Minuten vorher" },
  { v: 60, l: "1 Stunde vorher" },
  { v: 120, l: "2 Stunden vorher" },
  { v: 1440, l: "1 Tag vorher" },
];

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (local) => {
  if (!local) return null;
  return new Date(local).toISOString();
};

export const EventModal = ({ open, onClose, initialDate, event, categories = [], members = [], onSaved }) => {
  const { user } = useAuth();
  const isEdit = !!event?.id;
  const isManager = user?.role === "admin" || user?.role === "sales_manager";

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "customer_visit",
    starts_at: "",
    ends_at: "",
    location: "",
    owner_user_id: "",
    reminder_minutes_before: null,
    related_project_id: "",
  });
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notifyCustomer, setNotifyCustomer] = useState(false);
  const [lastNotification, setLastNotification] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (event) {
      setForm({
        title: event.title || "",
        description: event.description || "",
        category: event.category || "customer_visit",
        starts_at: toLocalInput(event.starts_at),
        ends_at: toLocalInput(event.ends_at),
        location: event.location || "",
        owner_user_id: event.owner_user_id || user?.id || "",
        reminder_minutes_before: event.reminder_minutes_before ?? null,
        related_project_id: event.related_project_id || "",
      });
    } else {
      const base = initialDate ? new Date(initialDate) : new Date();
      base.setMinutes(0); base.setSeconds(0);
      const end = new Date(base.getTime() + 60 * 60 * 1000);
      setForm({
        title: "",
        description: "",
        category: "customer_visit",
        starts_at: toLocalInput(base.toISOString()),
        ends_at: toLocalInput(end.toISOString()),
        location: "",
        owner_user_id: user?.id || "",
        reminder_minutes_before: null,
        related_project_id: "",
      });
    }
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
    setLastNotification(null);
    setNotifyCustomer(false);
  }, [open, event, initialDate, user]);

  if (!open) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Titel fehlt");
    if (!form.starts_at || !form.ends_at) return toast.error("Datum/Zeit fehlt");
    if (form.ends_at < form.starts_at) return toast.error("Ende vor Beginn");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        starts_at: fromLocalInput(form.starts_at),
        ends_at: fromLocalInput(form.ends_at),
        location: form.location.trim(),
        owner_user_id: form.owner_user_id || undefined,
        related_project_id: form.related_project_id || null,
        reminder_minutes_before: form.reminder_minutes_before,
        notify_customer: notifyCustomer && form.category === "customer_visit" && !!form.related_project_id,
      };
      if (isEdit) {
        await api.patch(`/calendar/events/${event.id}`, payload);
        toast.success("Termin aktualisiert");
        onSaved?.();
        onClose();
      } else {
        const { data } = await api.post("/calendar/events", payload);
        if (data.customer_notification?.notified) {
          setLastNotification(data.customer_notification);
          toast.success("Termin angelegt · Kunde benachrichtigt · Status: Vor-Ort-Aufnahme");
          onSaved?.();
          // Keep modal open briefly to show the tracking link
          return;
        }
        toast.success("Termin angelegt");
        onSaved?.();
        onClose();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!confirm("Termin wirklich löschen?")) return;
    try {
      await api.delete(`/calendar/events/${event.id}`);
      toast.success("Termin gelöscht");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="event-modal">
      <div className="bg-[#18181B] border border-zinc-800 rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#18181B] border-b border-zinc-800 px-5 py-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">{isEdit ? "Termin bearbeiten" : "Neuer Termin"}</h3>
          <button onClick={onClose} className="h-10 w-10 rounded-xl bg-zinc-800/60 hover:bg-zinc-800 flex items-center justify-center" data-testid="event-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider">Titel *</label>
            <input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              data-testid="event-title-input"
              className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              placeholder="z. B. Vor-Ort-Termin Familie Schmidt"
            />
          </div>

          <div>
            <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider">Kategorie</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c.key}
                  data-testid={`event-cat-${c.key}`}
                  onClick={() => update("category", c.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${form.category === c.key ? "border-[#00E575] text-[#09090B]" : "border-zinc-700 text-zinc-300 hover:border-zinc-600"}`}
                  style={form.category === c.key ? { backgroundColor: c.color } : { backgroundColor: "transparent" }}
                >
                  <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: c.color }} />
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider">Beginn *</label>
              <input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => update("starts_at", e.target.value)}
                data-testid="event-start-input"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              />
            </div>
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider">Ende *</label>
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => update("ends_at", e.target.value)}
                data-testid="event-end-input"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              />
            </div>
          </div>

          <div>
            <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5"><MapPin size={12} /> Ort</label>
            <input
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              data-testid="event-location-input"
              className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              placeholder="Adresse oder Raum"
            />
          </div>

          {isManager && members.length > 0 && (
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5"><UserIcon size={12} /> Für Benutzer</label>
              <select
                value={form.owner_user_id}
                onChange={(e) => update("owner_user_id", e.target.value)}
                data-testid="event-owner-select"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              >
                {members.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}{m.id === user?.id ? " (Ich)" : ""}</option>
                ))}
              </select>
            </div>
          )}

          {projects.length > 0 && (
            <div>
              <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5"><Folder size={12} /> Projekt (optional)</label>
              <select
                value={form.related_project_id || ""}
                onChange={(e) => update("related_project_id", e.target.value || "")}
                data-testid="event-project-select"
                className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
              >
                <option value="">— kein Projekt —</option>
                {projects.slice(0, 100).map((p) => (
                  <option key={p.id} value={p.id}>{p.project_number} · {p.customer_name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notify customer toggle – only meaningful for customer_visit linked to project */}
          {!isEdit && form.related_project_id && form.category === "customer_visit" && (
            <label className="flex items-start gap-3 bg-[#00E575]/8 border border-[#00E575]/30 rounded-xl p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyCustomer}
                onChange={(e) => setNotifyCustomer(e.target.checked)}
                data-testid="event-notify-customer-checkbox"
                className="mt-1 h-4 w-4 accent-[#00E575]"
              />
              <div className="text-xs leading-relaxed">
                <div className="font-semibold text-[#00E575] flex items-center gap-1.5"><Send size={12} /> Kunde benachrichtigen & Status setzen</div>
                <div className="text-zinc-400 mt-1">Projektstatus springt auf <span className="text-zinc-200 font-medium">Vor-Ort-Aufnahme</span>. Kunde erhält den Tracking-Link per E-Mail (Mock).</div>
              </div>
            </label>
          )}

          {lastNotification?.notified && (
            <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3" data-testid="event-notification-result">
              <div className="flex items-center gap-2 text-emerald-300 font-semibold text-sm">
                <CheckCircle2 size={16} /> Kunde benachrichtigt (MOCK)
              </div>
              <div className="mt-1 text-xs text-zinc-300">An: <span className="font-mono">{lastNotification.customer_email || "—"}</span></div>
              {lastNotification.tracking_url && (
                <a href={lastNotification.tracking_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs text-[#00E575] hover:underline break-all">{lastNotification.tracking_url}</a>
              )}
              <button onClick={onClose} className="mt-2 text-xs underline text-zinc-400 hover:text-zinc-200">Schließen</button>
            </div>
          )}

          <div>
            <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5"><Bell size={12} /> Erinnerung</label>
            <select
              value={form.reminder_minutes_before === null ? "" : String(form.reminder_minutes_before)}
              onChange={(e) => update("reminder_minutes_before", e.target.value === "" ? null : Number(e.target.value))}
              data-testid="event-reminder-select"
              className="mt-1 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-3 h-11 outline-none focus:border-[#00E575]"
            >
              {REMINDER_OPTIONS.map((o) => (
                <option key={String(o.v)} value={o.v === null ? "" : String(o.v)}>{o.l}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider flex items-center gap-1.5"><FileText size={12} /> Notiz</label>
            <DictaphoneTextarea
              value={form.description}
              onChange={(v) => update("description", v)}
              testid="event-description-input"
              rows={3}
              placeholder="Optional – tippen oder Mikrofon-Button drücken zum Diktieren"
              className="bg-[#09090B] border-zinc-700 text-zinc-100 focus:border-[#00E575] focus:ring-[#00E575] mt-1"
            />
          </div>

          <div className="flex items-center justify-between gap-2 pt-2">
            {isEdit ? (
              <button onClick={remove} data-testid="event-delete-btn" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 text-sm px-3 h-11 rounded-xl border border-red-500/30 hover:border-red-500/60">
                <Trash2 size={16} /> Löschen
              </button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <button onClick={onClose} data-testid="event-cancel-btn" className="px-4 h-11 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm">Abbrechen</button>
              <button onClick={save} disabled={saving} data-testid="event-save-btn" className="px-5 h-11 rounded-xl bg-[#00E575] text-[#09090B] hover:bg-[#00C964] font-semibold text-sm disabled:opacity-50">
                {saving ? "Speichert…" : isEdit ? "Speichern" : "Anlegen"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
